import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Col, Form, Button, Spinner, Badge } from 'react-bootstrap';
import { FaTrophy, FaFilter } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getApiBaseUrl } from '../../../utils/apiBase';
import BadgeIcon, { LearnerAvatar } from './BadgeIcon';
import UserPageHeader from '../UserPageHeader';

const PERIOD_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'all_time', label: 'All Time' },
  { value: 'custom', label: 'Custom Range' }
];

const PODIUM_STYLES = {
  1: 'ring-4 ring-yellow-400 bg-gradient-to-b from-yellow-50 to-white',
  2: 'ring-4 ring-gray-300 bg-gradient-to-b from-gray-50 to-white',
  3: 'ring-4 ring-amber-600 bg-gradient-to-b from-amber-50 to-white'
};

const LeaderboardView = ({
  title = 'Leaderboard',
  subtitle = '',
  iconClass = 'fas fa-trophy',
  variant = 'default'
}) => {
  const [entries, setEntries] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [period, setPeriod] = useState('all_time');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 1, total: 0 });

  const uploadsBaseUrl = useMemo(() => getApiBaseUrl().replace(/\/api\/?$/, ''), []);

  const fetchLeaderboard = useCallback(async ({ pageNum = 1, append = false } = {}) => {
    if (period === 'custom' && (!fromDate || !toDate)) {
      if (!append) setLoading(false);
      return;
    }

    if (append) setLoadingMore(true);
    else setLoading(true);

    try {
      const params = {
        period,
        page: pageNum,
        limit: 20
      };
      if (period === 'custom') {
        params.from_date = fromDate;
        params.to_date = toDate;
      }

      const response = await api.gamification.getLeaderboard(params);
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load leaderboard.');
        return;
      }

      const data = response.data.data || {};
      setEntries((prev) => (append ? [...prev, ...(data.entries || [])] : (data.entries || [])));
      setCurrentUser(data.current_user || null);
      setPagination(data.pagination || { totalPages: 1, total: 0 });
      setPage(pageNum);
    } catch (error) {
      toast.error(`Failed to load leaderboard: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [period, fromDate, toDate]);

  useEffect(() => {
    fetchLeaderboard({ pageNum: 1, append: false });
  }, [fetchLeaderboard]);

  const topThree = entries.slice(0, 3);
  const restEntries = entries.slice(3);

  const podiumOrder = topThree.length >= 3
    ? [topThree[1], topThree[0], topThree[2]]
    : topThree;

  const handleApplyFilters = () => {
    fetchLeaderboard({ pageNum: 1, append: false });
  };

  const handleLoadMore = () => {
    if (page < pagination.totalPages) {
      fetchLeaderboard({ pageNum: page + 1, append: true });
    }
  };

  const renderPodiumCard = (entry, sizeClass) => {
    if (!entry) return <div className="flex-1" />;

    const podiumClass = PODIUM_STYLES[entry.rank] || '';

    return (
      <div className={`flex flex-col items-center text-center p-3 rounded-2xl ${podiumClass} ${entry.is_current_user ? 'outline outline-2 outline-purple-500' : ''}`}>
        <div className="relative mb-2">
          <LearnerAvatar
            avatarPath={entry.avatar_path}
            firstName={entry.first_name}
            lastName={entry.last_name}
            sizeClass={sizeClass}
            uploadsBaseUrl={uploadsBaseUrl}
          />
          <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-indigo-700 text-white text-sm font-bold flex items-center justify-center border-2 border-white">
            {entry.rank}
          </span>
        </div>
        <p className="font-semibold text-gray-800 text-sm mb-1">{entry.full_name}</p>
        <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border">
          {entry.total_points}
        </Badge>
        <p className="text-xs text-gray-500 mt-1">{entry.current_level}</p>
      </div>
    );
  };

  const renderListRow = (entry) => (
    <div
      key={`${entry.user_id}-${entry.rank}`}
      className={`flex items-center gap-3 py-3 border-b border-gray-100 ${entry.is_current_user ? 'bg-purple-50 rounded-lg px-2' : ''}`}
    >
      <span className="w-8 h-8 rounded-full bg-indigo-700 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
        {entry.rank}
      </span>
      <LearnerAvatar
        avatarPath={entry.avatar_path}
        firstName={entry.first_name}
        lastName={entry.last_name}
        sizeClass="w-10 h-10"
        uploadsBaseUrl={uploadsBaseUrl}
      />
      <div className="flex-grow-1 min-w-0">
        <p className="font-medium text-gray-800 mb-0 truncate">{entry.full_name}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <BadgeIcon badgeKey={entry.badge_key} color={entry.badge_color} size={14} />
          <span>{entry.current_level}</span>
        </div>
      </div>
      <Badge bg="light" text="dark" className="px-3 py-2 rounded-pill border flex-shrink-0">
        {entry.total_points}
      </Badge>
    </div>
  );

  return (
    <div className={`min-h-screen ${variant === 'user' ? 'bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8 lg:p-10' : 'bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 p-3 sm:p-4 md:p-6'}`}>
      {variant === 'user' ? (
        <UserPageHeader
          iconClass={iconClass}
          title={title}
          subtitle={subtitle}
        />
      ) : (
        <div className="mb-4 text-center">
          <h4 className="fw-bold mb-1 text-xl sm:text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
            {title}
          </h4>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 border border-purple-100">
        <Row className="g-3 align-items-end">
          <Col xs={12} sm={6} lg={period === 'custom' ? 3 : 4}>
            <Form.Label className="text-sm font-semibold text-gray-600 mb-1">Time Period</Form.Label>
            <Form.Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="rounded-lg border-purple-200 shadow-sm focus:ring-2 focus:ring-purple-500 text-sm"
              aria-label="Select time period"
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Col>
          {period === 'custom' && (
            <>
              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="text-sm font-semibold text-gray-600 mb-1">From time period</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="rounded-lg border-purple-200 shadow-sm text-sm"
                  aria-label="From time period"
                />
              </Col>
              <Col xs={12} sm={6} lg={3}>
                <Form.Label className="text-sm font-semibold text-gray-600 mb-1">To time period</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="rounded-lg border-purple-200 shadow-sm text-sm"
                  aria-label="To time period"
                />
              </Col>
            </>
          )}
          <Col
            xs={12}
            sm={period === 'custom' ? 12 : 6}
            lg="auto"
            className="d-flex align-items-end justify-content-sm-end"
          >
            <Button
              type="button"
              bsPrefix="custom-hamburger"
              onClick={handleApplyFilters}
              disabled={loading || (period === 'custom' && (!fromDate || !toDate))}
              className="w-100 w-sm-auto border-1 border-purple-600 bg-white text-purple-600 hover:bg-purple-50 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm flex items-center justify-center gap-2 px-5 py-2 min-h-[38px]"
              aria-label="Apply leaderboard filters"
            >
              <FaFilter className="w-4 h-4 flex-shrink-0" />
              <span>Apply</span>
            </Button>
          </Col>
        </Row>
      </div>

      {currentUser && !entries.some((entry) => entry.is_current_user) && (
        <div className="bg-indigo-700 text-white rounded-2xl p-4 mb-4 shadow-lg">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="mb-1 text-indigo-100 text-sm">Your Ranking</p>
              <p className="mb-0 font-bold text-lg">#{currentUser.rank || '-'} · {currentUser.full_name}</p>
            </div>
            <div className="text-end">
              <p className="mb-0 text-2xl font-bold">{currentUser.total_points}</p>
              <p className="mb-0 text-indigo-100 text-sm">{currentUser.current_level}</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16">
          <Spinner animation="border" className="text-purple-600" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow border border-purple-100">
          <FaTrophy className="text-purple-300 mb-3" size={48} />
          <p className="text-gray-600 mb-0">No leaderboard data available for this period.</p>
        </div>
      ) : (
        <>
          {topThree.length > 0 && (
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 border border-purple-100">
              <div className="flex items-end justify-center gap-3 sm:gap-6">
                <div className="flex-1 max-w-[140px]">{renderPodiumCard(podiumOrder[0], 'w-16 h-16 sm:w-20 sm:h-20')}</div>
                <div className="flex-1 max-w-[160px] -mt-4">{renderPodiumCard(podiumOrder[1], 'w-20 h-20 sm:w-24 sm:h-24')}</div>
                <div className="flex-1 max-w-[140px]">{renderPodiumCard(podiumOrder[2], 'w-16 h-16 sm:w-20 sm:h-20')}</div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-lg p-4 border border-purple-100">
            {restEntries.map(renderListRow)}
            {entries.length <= 3 && topThree.map(renderListRow)}
          </div>

          {page < pagination.totalPages && (
            <div className="text-center mt-4">
              <Button
                type="button"
                bsPrefix="custom-hamburger"
                onClick={handleLoadMore}
                disabled={loadingMore}
                className="border-1 border-purple-600 bg-white text-purple-600 hover:bg-purple-50 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-sm flex items-center justify-center gap-2 px-5 py-2 mx-auto"
              >
                {loadingMore ? <Spinner animation="border" size="sm" /> : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeaderboardView;
